import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Unlock, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const GROUPS = [
  { key: "tools", title: "Tools", items: [
    { to: "/co-analysis", label: "Copilot", desc: "Analyse a market together with TRADECRAFT and compare reads", lock: "open" },
    { to: "/analyst", label: "Analyst", desc: "TRADECRAFT does the professional analysis; you verify and challenge it", lock: "analyst" },
    { to: "/scenarios", label: "Scenario Library", desc: "Browse historical scenarios and themed packs to replay" },
    { to: "/indicator-lab", label: "Indicator Lab", desc: "Try indicators on real charts and see what they show" },
    { to: "/risk-lab", label: "Risk Lab", desc: "Practise position sizing and risk on sample trades" },
  ] },
  { key: "library", title: "Library", items: [
    { to: "/learn", label: "Learn", desc: "Short lessons and explainers" },
    { to: "/glossary", label: "Glossary", desc: "Plain definitions of trading terms" },
    { to: "/intelligence", label: "Intelligence", desc: "Market context and briefings", lock: "intel" },
  ] },
  { key: "day", title: "Your day", items: [
    { to: "/dashboard", label: "Daily", desc: "Your daily edge and drills", testid: "daily" },
  ] },
  { key: "progress", title: "Your progress", items: [
    { to: "/roadmap", label: "Roadmap", desc: "Where you are and where you're going in TRADECRAFT" },
    { to: "/journal", label: "Journal", desc: "Your trade notes and reflections" },
    { to: "/performance", label: "Performance", desc: "Your results over time" },
    { to: "/skills", label: "Skills", desc: "How far along you are on each trading skill" },
    { to: "/achievements", label: "Achievements", desc: "Badges and milestones you've earned" },
    { to: "/my-calls", label: "My Calls", desc: "Call cards from your best decisions" },
    { to: "/risk-profile", label: "Risk Profile", desc: "Your risk style and limits" },
  ] },
  { key: "account", title: "Account", items: [
    { to: "/family", label: "Family", desc: "Link family members and manage access" },
    { to: "/family/week", label: "Family Week", desc: "Your family's training week at a glance", show: "linked" },
    { to: "/parent", label: "Guardian Dashboard", desc: "Follow and support the learners you guard", show: "guardian" },
    { to: "/assess", label: "Assess My Skills", desc: "Test out of beginner missions by proving what you know" },
    { to: "/account", label: "Account & Billing", desc: "Your plan, billing and receipts" },
    { to: "/pricing", label: "Upgrade", desc: "Compare plans and upgrade" },
    { to: "/support", label: "Support", desc: "Get help" },
    { to: "/settings", label: "Settings", desc: "Profile, frames, chart themes and account" },
  ] },
  { key: "admin", title: "Admin", show: "admin", items: [
    { to: "/quality", label: "Intelligence Quality", desc: "Review the quality of intelligence output" },
    { to: "/admin/organic", label: "Organic Intelligence", desc: "Manage organic intelligence research" },
    { to: "/admin/comp", label: "Plan Comps", desc: "Grant complimentary plans to accounts" },
    { to: "/admin/support", label: "Support Submissions", desc: "Every message sent through the public Support form" },
  ] },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function visibility(user) {
  const students = user?.student_ids?.length || 0;
  return {
    linked: students > 0 || (user?.guardian_ids?.length || 0) > 0,
    guardian: students > 0 || user?.role === "parent" || user?.role === "admin",
    admin: !!user?.is_admin,
  };
}

const LockBadge = ({ state, id }) => {
  if (!state) return null;
  const open = state === "open";
  const Icon = open ? Unlock : Lock;
  return (
    <span data-testid={`more-${id}-lock-state`}
      className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${
        open ? "text-tc-bull border-tc-bull/30 bg-tc-bull/10" : "text-tc-mastery border-tc-mastery/30 bg-tc-mastery/10"}`}>
      <Icon className="w-3 h-3" /> {open ? "Unlocked" : "Locked"}
    </span>
  );
};

const Entry = ({ item, lockState }) => {
  const id = item.testid || slug(item.label);
  return (
    <Link to={item.to} data-testid={`more-link-${id}`}
      className="group flex items-center justify-between gap-4 px-4 py-3.5 rounded-lg border border-transparent hover:border-white/10 hover:bg-white/5 transition-colors">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-tc-txt">{item.label}</span>
          <LockBadge state={lockState} id={id} />
        </div>
        <div className="text-sm text-tc-muted mt-0.5">{item.desc}</div>
      </div>
      <ChevronRight className="w-4 h-4 shrink-0 text-tc-muted group-hover:text-tc-txt transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
};

export default function More() {
  const { user } = useAuth();
  const vis = visibility(user);
  const [locks, setLocks] = useState({ open: "open" });

  useEffect(() => {
    const check = (key, url) => api.get(url)
      .then((r) => setLocks((l) => ({ ...l, [key]: r.data.authorization?.authorized ? "open" : "locked" })))
      .catch(() => setLocks((l) => ({ ...l, [key]: "locked" })));
    check("analyst", "/analyst/authorization");
    check("intel", "/intel/authorization");
  }, []);

  const lockFor = (item) => item.lock && locks[item.lock];
  const groups = GROUPS.filter((g) => !g.show || vis[g.show])
    .map((g) => ({ ...g, items: g.items.filter((i) => !i.show || vis[i.show]) }));

  return (
    <div className="p-5 sm:p-8 max-w-3xl" data-testid="more-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">More</h1>
      <p className="text-tc-sub mt-2">Everything else in TRADECRAFT, grouped.</p>
      <div className="mt-8 space-y-8">
        {groups.map((g) => (
          <section key={g.key} data-testid={`more-group-${g.key}`}>
            <h2 className="text-[11px] font-mono uppercase tracking-widest text-tc-muted px-4 mb-2">{g.title}</h2>
            <div className="tc-card p-1.5 divide-y divide-white/5">
              {g.items.map((i) => <Entry key={i.to + i.label} item={i} lockState={lockFor(i)} />)}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
